// Helper function to get showcase image URL
const getShowcaseImageUrl = (filename: string): string => {
	const basePath = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');
	return `${basePath}/showcase/${filename}`;
};

export interface ShowcaseProject {
	id: string;
	title: string;
	style: string;
	location: string;
	client: string;
	completedDate: string;
	before: string;
	after: string;
	description: string;
	details: {
		area: string;
		duration: string;
		budget: string;
		plants: string[];
		features: string[];
		challenges: string[];
		solutions: string[];
		testimonial: string;
	};
}

const showcaseProjectsEn: ShowcaseProject[] = [
	{
		id: 'scandinavian-courtyard',
		title: 'Modern Courtyard Garden',
		style: 'Modern contemporary',
		location: 'Munich, Germany',
		client: 'Emma & James',
		completedDate: '2025-05-02',
		before: getShowcaseImageUrl('scandinavian-courtyard-before.png'),
		after: getShowcaseImageUrl('scandinavian-courtyard-after.png'),
		description: 'A simple lawn transformed into a clean outdoor room for dining, lounging, and evening atmosphere.',
		details: {
			area: '110 m²',
			duration: '4 weeks',
			budget: '€16,900',
			plants: [
				'Portuguese laurel hedge (Prunus lusitanica)',
				'Lavender (Lavandula angustifolia)',
				'Hydrangea (Hydrangea paniculata)',
				'Ornamental alliums (Allium giganteum)',
				'Olive tree in container (Olea europaea)'
			],
			features: [
				'Large-format porcelain paving in a minimalist grid',
				'Built-in outdoor kitchen with prep and storage',
				'Dining terrace plus separate lounge zone',
				'Integrated warm lighting for evening use',
				'Simple lawn core for an open, spacious feel'
			],
			challenges: [
				'Flat lawn lacked function and visual structure',
				'Clients wanted low-maintenance planting and clean lines',
				'Space needed to work equally well day and night'
			],
			solutions: [
				'Created clear hardscape geometry to zone the garden',
				'Used evergreen structure with soft perennial accents',
				'Layered task and ambient lighting for usability after sunset'
			],
			testimonial: 'It feels like an extra living room outside. We use the kitchen and seating area almost every evening, and the garden still feels calm and easy to maintain.'
		}
	},
	{
		id: 'romantic-garden',
		title: 'Romantic Cottage Garden',
		style: 'Romantic cottage',
		location: 'Osijek, Croatia',
		client: 'Olivia & Ben',
		completedDate: '2025-05-05',
		before: getShowcaseImageUrl('romantic-garden-before.png'),
		after: getShowcaseImageUrl('romantic-garden-after.png'),
		description: 'A plain lawn reimagined as a soft, flower-rich family retreat with layered seating zones.',
		details: {
			area: '135 m²',
			duration: '5 weeks',
			budget: '€19,600',
			plants: [
				'Climbing roses (Rosa climbing hybrids)',
				'Lavender (Lavandula angustifolia)',
				'Wisteria (Wisteria sinensis)',
				'Foxglove (Digitalis purpurea)',
				'Hydrangea (Hydrangea arborescens)'
			],
			features: [
				'Timber swing frame as a focal lounge feature',
				'Curved gravel and stepping-stone pathways',
				'Fire table seating area for social evenings',
				'Layered perennial borders with long flowering seasons',
				'String lighting to create a warm cottage atmosphere'
			],
			challenges: [
				'Large empty lawn felt exposed and underused',
				'Clients wanted a relaxed, romantic look without heavy upkeep',
				'Needed family-friendly circulation between seating areas'
			],
			solutions: [
				'Introduced soft planting structure along all boundaries',
				'Used repeating hardy perennials for color and easy maintenance',
				'Linked zones with intuitive paths and low-level lighting'
			],
			testimonial: 'It feels like our own little countryside escape. The swing, flowers, and evening lights made the garden somewhere we actually spend time every day.'
		}
	},
	{
		id: 'modern-native',
		title: 'Scandinavian Family Garden',
		style: 'Scandinavian contemporary',
		location: 'Hamburg, Germany',
		client: 'Sophie & Daniel',
		completedDate: '2025-04-18',
		before: getShowcaseImageUrl('modern-native-before.png'),
		after: getShowcaseImageUrl('modern-native-after.png'),
		description: 'Clean paving, soft planting, and warm outdoor living zones for everyday family use.',
		details: {
			area: '140 m²',
			duration: '5 weeks',
			budget: '€21,400',
			plants: [
				'Hydrangeas (Hydrangea macrophylla)',
				'Lavender (Lavandula angustifolia)',
				'Salvia (Salvia nemorosa)',
				'Ornamental grasses (Calamagrostis x acutiflora)',
				'Multi-stem shade tree (Acer campestre)'
			],
			features: [
				'Light porcelain paving with clear geometric layout',
				'Outdoor kitchen and dining terrace',
				'Sunken hot-tub deck for private relaxation',
				'Layered perennial borders for four-season color',
				'Low-glare path lighting for evening use'
			],
			challenges: [
				'Featureless lawn with no functional zones',
				'Need for both entertaining and child-friendly open space',
				'Clients wanted a calm design that still felt lush'
			],
			solutions: [
				'Split the garden into clear activity areas with flowing paths',
				'Balanced evergreen structure with long-blooming perennials',
				'Used durable, easy-care materials and automated irrigation'
			],
			testimonial: 'We wanted a Scandinavian look without the garden feeling cold. Now it is practical, elegant, and we use every corner from morning coffee to evening dinners.'
		}
	},
	{
		id: 'mediterranean-retreat',
		title: 'Mediterranean Retreat',
		style: 'Mediterranean retreat',
		location: 'Split, Croatia',
		client: 'Marina & Ivan',
		completedDate: '2024-06-15',
		before: getShowcaseImageUrl('mediterranean-retreat-before.png'),
		after: getShowcaseImageUrl('mediterranean-retreat-after.jpg'),
		description: 'Olive grove patio with terracotta textures and drought-friendly planting.',
		details: {
			area: '120 m²',
			duration: '6 weeks',
			budget: '€18,500',
			plants: [
				'Olive trees (Olea europaea)',
				'Lavender (Lavandula angustifolia)',
				'Rosemary (Rosmarinus officinalis)',
				'Agapanthus (Agapanthus africanus)',
				'Bougainvillea (Bougainvillea spectabilis)'
			],
			features: [
				'Terracotta tile patio with seating area',
				'Drought-resistant native Mediterranean plants',
				'Automated drip irrigation system',
				'Stone pathway with herb garden borders',
				'Outdoor kitchen prep area with pergola'
			],
			challenges: [
				'Limited water availability in summer months',
				'Rocky soil requiring soil amendment',
				'Sloping terrain requiring terracing'
			],
			solutions: [
				'Installed rainwater collection system',
				'Used raised beds with quality soil mix',
				'Created tiered levels with retaining walls'
			],
			testimonial: 'We wanted a low-maintenance garden that reminded us of our trips to the Mediterranean. The team delivered exactly that - beautiful, sustainable, and easy to care for!'
		}
	},
	{
		id: 'urban-sanctuary',
		title: 'Urban Sanctuary',
		style: 'Urban sanctuary',
		location: 'Rijeka, Croatia',
		client: 'Petra & Marko',
		completedDate: '2024-09-10',
		before: getShowcaseImageUrl('urban-sanctuary-before.png'),
		after: getShowcaseImageUrl('urban-sanctuary-after.jpg'),
		description: 'Pocket garden with vertical planters, lounge deck, and smart irrigation.',
		details: {
			area: '45 m²',
			duration: '3 weeks',
			budget: '€9,800',
			plants: [
				'Vertical garden mix (ferns, herbs, succulents)',
				'Climbing ivy (Hedera helix)',
				'Herb collection (basil, mint, thyme, oregano)',
				'Dwarf fruit trees (citrus in containers)',
				'Seasonal flowers (pansies, petunias)'
			],
			features: [
				'Vertical living wall system',
				'Raised deck with built-in seating',
				'Smart irrigation with moisture sensors',
				'Container garden for flexibility',
				'LED grow lights for year-round herbs'
			],
			challenges: [
				'Very limited ground space',
				'Shaded area requiring shade-tolerant plants',
				'Need for privacy from neighboring buildings'
			],
			solutions: [
				'Maximized vertical space with wall planters',
				'Selected shade-loving plants and herbs',
				'Created green screen with climbing plants'
			],
			testimonial: 'We never thought we could have a real garden in such a small space. Now we grow our own herbs and have a beautiful green oasis right in the city center!'
		}
	}
];

type GalleryLanguage = 'en' | 'de' | 'hr';

const textByLanguage: Record<
	GalleryLanguage,
	Record<
		string,
		{
			title: string;
			style: string;
			description: string;
			client: string;
			duration: string;
			features: string[];
			challenges: string[];
			solutions: string[];
			testimonial: string;
		}
	>
> = {
	en: {
		'scandinavian-courtyard': {
			title: 'Modern Courtyard Garden',
			style: 'Modern contemporary',
			description: 'A simple lawn transformed into a clean outdoor room for dining, lounging, and evening atmosphere.',
			client: 'Emma & James',
			duration: '4 weeks',
			features: [
				'Large-format porcelain paving in a minimalist grid',
				'Built-in outdoor kitchen with prep and storage',
				'Dining terrace plus separate lounge zone',
				'Integrated warm lighting for evening use',
				'Simple lawn core for an open, spacious feel'
			],
			challenges: [
				'Flat lawn lacked function and visual structure',
				'Clients wanted low-maintenance planting and clean lines',
				'Space needed to work equally well day and night'
			],
			solutions: [
				'Created clear hardscape geometry to zone the garden',
				'Used evergreen structure with soft perennial accents',
				'Layered task and ambient lighting for usability after sunset'
			],
			testimonial:
				'It feels like an extra living room outside. We use the kitchen and seating area almost every evening, and the garden still feels calm and easy to maintain.'
		},
		'romantic-garden': {
			title: 'Romantic Cottage Garden',
			style: 'Romantic cottage',
			description: 'A plain lawn reimagined as a soft, flower-rich family retreat with layered seating zones.',
			client: 'Olivia & Ben',
			duration: '5 weeks',
			features: [
				'Timber swing frame as a focal lounge feature',
				'Curved gravel and stepping-stone pathways',
				'Fire table seating area for social evenings',
				'Layered perennial borders with long flowering seasons',
				'String lighting to create a warm cottage atmosphere'
			],
			challenges: [
				'Large empty lawn felt exposed and underused',
				'Clients wanted a relaxed, romantic look without heavy upkeep',
				'Needed family-friendly circulation between seating areas'
			],
			solutions: [
				'Introduced soft planting structure along all boundaries',
				'Used repeating hardy perennials for color and easy maintenance',
				'Linked zones with intuitive paths and low-level lighting'
			],
			testimonial:
				'It feels like our own little countryside escape. The swing, flowers, and evening lights made the garden somewhere we actually spend time every day.'
		},
		'mediterranean-retreat': {
			title: 'Mediterranean Retreat',
			style: 'Mediterranean retreat',
			description: 'Olive grove patio with terracotta textures and drought-friendly planting.',
			client: 'Marina & Ivan',
			duration: '6 weeks',
			features: [
				'Terracotta tile patio with seating area',
				'Drought-resistant native Mediterranean plants',
				'Automated drip irrigation system',
				'Stone pathway with herb garden borders',
				'Outdoor kitchen prep area with pergola'
			],
			challenges: [
				'Limited water availability in summer months',
				'Rocky soil requiring soil amendment',
				'Sloping terrain requiring terracing'
			],
			solutions: [
				'Installed rainwater collection system',
				'Used raised beds with quality soil mix',
				'Created tiered levels with retaining walls'
			],
			testimonial:
				'We wanted a low-maintenance garden that reminded us of our trips to the Mediterranean. The team delivered exactly that - beautiful, sustainable, and easy to care for!'
		},
		'modern-native': {
			title: 'Scandinavian Family Garden',
			style: 'Scandinavian contemporary',
			description: 'Clean paving, soft planting, and warm outdoor living zones for everyday family use.',
			client: 'Sophie & Daniel',
			duration: '5 weeks',
			features: [
				'Light porcelain paving with clear geometric layout',
				'Outdoor kitchen and dining terrace',
				'Sunken hot-tub deck for private relaxation',
				'Layered perennial borders for four-season color',
				'Low-glare path lighting for evening use'
			],
			challenges: [
				'Featureless lawn with no functional zones',
				'Need for both entertaining and child-friendly open space',
				'Clients wanted a calm design that still felt lush'
			],
			solutions: [
				'Split the garden into clear activity areas with flowing paths',
				'Balanced evergreen structure with long-blooming perennials',
				'Used durable, easy-care materials and automated irrigation'
			],
			testimonial:
				'We wanted a Scandinavian look without the garden feeling cold. Now it is practical, elegant, and we use every corner from morning coffee to evening dinners.'
		},
		'urban-sanctuary': {
			title: 'Urban Sanctuary',
			style: 'Urban sanctuary',
			description: 'Pocket garden with vertical planters, lounge deck, and smart irrigation.',
			client: 'Petra & Marko',
			duration: '3 weeks',
			features: [
				'Vertical living wall system',
				'Raised deck with built-in seating',
				'Smart irrigation with moisture sensors',
				'Container garden for flexibility',
				'LED grow lights for year-round herbs'
			],
			challenges: [
				'Very limited ground space',
				'Shaded area requiring shade-tolerant plants',
				'Need for privacy from neighboring buildings'
			],
			solutions: [
				'Maximized vertical space with wall planters',
				'Selected shade-loving plants and herbs',
				'Created green screen with climbing plants'
			],
			testimonial:
				'We never thought we could have a real garden in such a small space. Now we grow our own herbs and have a beautiful green oasis right in the city center!'
		}
	},
	de: {
		'scandinavian-courtyard': {
			title: 'Moderner Innenhofgarten',
			style: 'Modern zeitgenössisch',
			description: 'Eine einfache Rasenfläche wurde zu einem klaren Outdoor-Wohnraum für Essen, Loungen und stimmungsvolle Abende.',
			client: 'Emma & James',
			duration: '4 Wochen',
			features: [
				'Großformatige Feinsteinzeugplatten im minimalistischen Raster',
				'Integrierte Außenküche mit Vorbereitungs- und Stauraum',
				'Essbereich plus separater Lounge-Zone',
				'Warme, integrierte Beleuchtung für den Abend',
				'Klare Rasenfläche für ein offenes, ruhiges Raumgefühl'
			],
			challenges: [
				'Die ebene Rasenfläche hatte weder Struktur noch Funktion',
				'Gewünscht waren pflegeleichte Pflanzen und klare Linien',
				'Der Garten sollte tagsüber und abends gleichermaßen funktionieren'
			],
			solutions: [
				'Klare Geometrie der Belagsflächen zur Zonierung des Gartens',
				'Immergrüne Struktur mit weichen Stauden kombiniert',
				'Aufgaben- und Ambientelicht für die Nutzung nach Sonnenuntergang'
			],
			testimonial:
				'Es fühlt sich wie ein zusätzliches Wohnzimmer im Freien an. Wir nutzen Küche und Sitzbereich fast jeden Abend und der Garten bleibt trotzdem ruhig und pflegeleicht.'
		},
		'romantic-garden': {
			title: 'Romantischer Cottage-Garten',
			style: 'Romantischer Cottage-Stil',
			description: 'Ein schlichter Rasen wurde zu einem blütenreichen Familiengarten mit weichen Aufenthaltszonen umgestaltet.',
			client: 'Olivia & Ben',
			duration: '5 Wochen',
			features: [
				'Holzschaukel als markanter Lounge-Fokus',
				'Geschwungene Kies- und Trittsteinwege',
				'Sitzbereich mit Feuertisch für gesellige Abende',
				'Mehrschichtige Staudenbeete mit langer Blütezeit',
				'Lichterketten für eine warme Cottage-Atmosphäre'
			],
			challenges: [
				'Große leere Rasenfläche wirkte ungenutzt und offen',
				'Gewünscht war ein romantischer Look mit wenig Pflegeaufwand',
				'Familienfreundliche Wege zwischen den Sitzbereichen waren nötig'
			],
			solutions: [
				'Ränder mit weicher, strukturierender Bepflanzung gefasst',
				'Robuste, wiederkehrende Stauden für Farbe und einfache Pflege',
				'Zonen mit intuitiven Wegen und niedriger Beleuchtung verbunden'
			],
			testimonial:
				'Es fühlt sich an wie unser kleines Landhaus-Refugium. Schaukel, Blumen und Abendlicht machen den Garten zu einem Ort, den wir jeden Tag wirklich nutzen.'
		},
		'mediterranean-retreat': {
			title: 'Mediterraner Rückzugsort',
			style: 'Mediterraner Rückzugsort',
			description: 'Olivenhof-Terrasse mit Terrakotta-Texturen und trockenheitsverträglicher Bepflanzung.',
			client: 'Marina & Ivan',
			duration: '6 Wochen',
			features: [
				'Sitzterrasse mit Terrakottafliesen',
				'Trockenheitsresistente mediterrane Pflanzen',
				'Automatisches Tropfbewässerungssystem',
				'Steinweg mit Kräuterbeet-Rändern',
				'Außenküchenbereich mit Pergola'
			],
			challenges: [
				'Begrenzte Wasserverfügbarkeit in den Sommermonaten',
				'Steiniger Boden mit notwendiger Bodenverbesserung',
				'Hanglage erforderte Terrassierung'
			],
			solutions: [
				'Regenwassersammelsystem installiert',
				'Hochbeete mit hochwertiger Erde angelegt',
				'Gestufte Ebenen mit Stützmauern geschaffen'
			],
			testimonial:
				'Wir wollten einen pflegeleichten Garten, der uns an unsere Mittelmeerreisen erinnert. Das Team hat genau das geliefert - schön, nachhaltig und leicht zu pflegen!'
		},
		'modern-native': {
			title: 'Skandinavischer Familiengarten',
			style: 'Skandinavisch modern',
			description: 'Klare Beläge, weiche Bepflanzung und warme Outdoor-Zonen für den Familienalltag.',
			client: 'Sophie & Daniel',
			duration: '5 Wochen',
			features: [
				'Helle Feinsteinzeugflächen mit klarer Geometrie',
				'Außenküche und Ess-Terrasse',
				'Abgesenktes Whirlpool-Deck für Privatsphäre',
				'Mehrschichtige Staudenbeete für Farbe in allen Jahreszeiten',
				'Blendfreie Wegbeleuchtung für den Abend'
			],
			challenges: [
				'Funktionslose Rasenfläche ohne klare Zonen',
				'Bedarf an Fläche für Gäste und kinderfreundliches Spielen',
				'Ruhiges Design gewünscht, das trotzdem üppig wirkt'
			],
			solutions: [
				'Garten in klare Aktivitätsbereiche mit fließenden Wegen gegliedert',
				'Immergrüne Struktur mit langblühenden Stauden kombiniert',
				'Langlebige, pflegeleichte Materialien und automatische Bewässerung eingesetzt'
			],
			testimonial:
				'Wir wollten einen skandinavischen Stil, ohne dass der Garten kühl wirkt. Jetzt ist er praktisch, elegant und wir nutzen jede Ecke vom Morgenkaffee bis zum Abendessen.'
		},
		'urban-sanctuary': {
			title: 'Urbanes Refugium',
			style: 'Urbanes Refugium',
			description: 'Pocket-Garten mit vertikalen Pflanzwänden, Lounge-Deck und smarter Bewässerung.',
			client: 'Petra & Marko',
			duration: '3 Wochen',
			features: [
				'Vertikales Living-Wall-System',
				'Erhöhtes Deck mit integrierten Sitzgelegenheiten',
				'Smarte Bewässerung mit Feuchtigkeitssensoren',
				'Container-Garten für flexible Nutzung',
				'LED-Pflanzenlichter für Kräuter ganzjährig'
			],
			challenges: [
				'Sehr begrenzte Bodenfläche',
				'Schattiger Bereich erforderte schattenverträgliche Pflanzen',
				'Bedarf an Sichtschutz zu Nachbargebäuden'
			],
			solutions: [
				'Vertikalen Raum mit Wandpflanzern maximal genutzt',
				'Schattenliebende Pflanzen und Kräuter ausgewählt',
				'Grünen Sichtschutz mit Kletterpflanzen geschaffen'
			],
			testimonial:
				'Wir hätten nie gedacht, dass wir auf so kleinem Raum einen echten Garten haben können. Jetzt bauen wir eigene Kräuter an und haben eine wunderschöne grüne Oase mitten in der Stadt!'
		}
	},
	hr: {
		'scandinavian-courtyard': {
			title: 'Moderni dvorišni vrt',
			style: 'Moderni suvremeni',
			description: 'Jednostavan travnjak pretvoren je u čist vanjski dnevni prostor za blagovanje, opuštanje i ugodne večeri.',
			client: 'Emma i James',
			duration: '4 tjedna',
			features: [
				'Velike porculanske ploče u minimalističkoj geometriji',
				'Ugrađena vanjska kuhinja s radnom plohom i spremištem',
				'Zona za blagovanje plus odvojeni lounge dio',
				'Topla integrirana rasvjeta za večernje korištenje',
				'Jednostavna središnja travnata površina za otvoren osjećaj prostora'
			],
			challenges: [
				'Ravan travnjak bez funkcije i jasne strukture',
				'Klijenti su željeli nisko održavanje i čiste linije',
				'Prostor je trebao dobro funkcionirati i danju i navečer'
			],
			solutions: [
				'Jasnom geometrijom popločenja podijeljene su funkcionalne zone',
				'Kombinirana zimzelena struktura s mekšim trajnicama',
				'Slojevita funkcionalna i ambijentalna rasvjeta za večernju upotrebu'
			],
			testimonial:
				'Djelo je kao dodatni dnevni boravak na otvorenom. Kuhinju i sjedeću zonu koristimo gotovo svaku večer, a vrt je i dalje miran i jednostavan za održavanje.'
		},
		'romantic-garden': {
			title: 'Romantični cottage vrt',
			style: 'Romantični cottage stil',
			description: 'Jednostavan travnjak pretvoren je u bogato cvjetni obiteljski vrt s ugodnim zonama za boravak.',
			client: 'Olivia i Ben',
			duration: '5 tjedana',
			features: [
				'Drvena ljuljačka kao glavni lounge element',
				'Zakrivljene staze od šljunka i kamenih ploča',
				'Zona za sjedenje s vatrenim stolom za večernja druženja',
				'Slojevite trajnice s dugom sezonom cvatnje',
				'Lampice za toplu cottage atmosferu'
			],
			challenges: [
				'Velik prazan travnjak djelovao je izloženo i neiskorišteno',
				'Klijenti su htjeli romantičan izgled uz malo održavanja',
				'Bila je potrebna obiteljski prilagođena povezanost zona'
			],
			solutions: [
				'Uvedena je mekana rubna sadnja koja strukturira prostor',
				'Korištene su izdržljive trajnice koje se ponavljaju radi boje i lakše njege',
				'Zone su povezane intuitivnim stazama i niskom rasvjetom'
			],
			testimonial:
				'Djeluje kao naše malo seosko utočište. Ljuljačka, cvijeće i večernja rasvjeta pretvorili su vrt u mjesto koje koristimo svaki dan.'
		},
		'mediterranean-retreat': {
			title: 'Mediteransko utočište',
			style: 'Mediteransko utočište',
			description: 'Terasa u masliniku s teksturama terakote i sadnjom otpornom na sušu.',
			client: 'Marina i Ivan',
			duration: '6 tjedana',
			features: [
				'Terasa od terakota pločica sa sjedećom zonom',
				'Mediteranske autohtone biljke otporne na sušu',
				'Automatizirani sustav navodnjavanja kap po kap',
				'Kamena staza s rubovima od začinskog bilja',
				'Vanjska kuhinjska zona s pergolom'
			],
			challenges: [
				'Ograničena dostupnost vode tijekom ljeta',
				'Kamenito tlo koje je zahtijevalo poboljšanje',
				'Nagnut teren koji je zahtijevao terasiranje'
			],
			solutions: [
				'Ugrađen sustav prikupljanja kišnice',
				'Postavljene povišene gredice s kvalitetnim supstratom',
				'Kreirane stepenaste razine s potpornim zidovima'
			],
			testimonial:
				'Htjeli smo vrt koji je jednostavan za održavanje i podsjeća nas na putovanja Mediteranom. Tim je isporučio upravo to - lijepo, održivo i lako za održavanje!'
		},
		'modern-native': {
			title: 'Skandinavski obiteljski vrt',
			style: 'Skandinavski suvremeni',
			description: 'Čiste popločane površine, meka sadnja i tople vanjske zone za svakodnevni obiteljski život.',
			client: 'Sophie i Daniel',
			duration: '5 tjedana',
			features: [
				'Svijetli porculanski opločnik s jasnom geometrijom',
				'Vanjska kuhinja i terasa za blagovanje',
				'Udubljena whirlpool zona za privatno opuštanje',
				'Slojevite trajnice za boju kroz sva godišnja doba',
				'Diskretna rasvjeta staza za večernje korištenje'
			],
			challenges: [
				'Neiskorišten travnjak bez funkcionalnih zona',
				'Potreban prostor i za druženje i za djecu',
				'Klijenti su željeli miran dizajn koji i dalje djeluje bujno'
			],
			solutions: [
				'Vrt je podijeljen u jasne funkcionalne cjeline s povezanim stazama',
				'Uravnotežena zimzelena struktura s dugo cvatućim trajnicama',
				'Korišteni trajni materijali jednostavni za održavanje i automatsko navodnjavanje'
			],
			testimonial:
				'Htjeli smo skandinavski izgled bez hladnog dojma vrta. Sada je praktičan, elegantan i koristimo svaki kutak od jutarnje kave do večere.'
		},
		'urban-sanctuary': {
			title: 'Urbano utočište',
			style: 'Urbano utočište',
			description: 'Mali gradski vrt s vertikalnim žardinjerama, lounge podestom i pametnim navodnjavanjem.',
			client: 'Petra i Marko',
			duration: '3 tjedna',
			features: [
				'Vertikalni sustav živog zida',
				'Povišeni podest s ugrađenim sjedenjem',
				'Pametno navodnjavanje sa senzorima vlage',
				'Kontejnerski vrt za veću fleksibilnost',
				'LED rasvjeta za uzgoj začina tijekom cijele godine'
			],
			challenges: [
				'Vrlo ograničen prostor tla',
				'Sjenovito područje koje traži biljke za sjenu',
				'Potreba za privatnošću od susjednih zgrada'
			],
			solutions: [
				'Maksimalno iskorišten vertikalni prostor zidnim sadilicama',
				'Odabrane biljke i začini koji podnose sjenu',
				'Formiran zeleni paravan penjačicama'
			],
			testimonial:
				'Nismo vjerovali da na ovako malom prostoru možemo imati pravi vrt. Sada uzgajamo vlastito začinsko bilje i imamo prekrasnu zelenu oazu u centru grada!'
		}
	}
};

export function getShowcaseProjects(language: GalleryLanguage = 'en'): ShowcaseProject[] {
	const localized = textByLanguage[language] ?? textByLanguage.en;
	return showcaseProjectsEn.map((project) => {
		const localizedText = localized[project.id] ?? textByLanguage.en[project.id];
		return {
			...project,
			title: localizedText?.title ?? project.title,
			style: localizedText?.style ?? project.style,
			description: localizedText?.description ?? project.description,
			client: localizedText?.client ?? project.client,
			details: {
				...project.details,
				duration: localizedText?.duration ?? project.details.duration,
				features: localizedText?.features ?? project.details.features,
				challenges: localizedText?.challenges ?? project.details.challenges,
				solutions: localizedText?.solutions ?? project.details.solutions,
				testimonial: localizedText?.testimonial ?? project.details.testimonial
			}
		};
	});
}

// Backward-compatible default export for existing pages that
// still import `showcaseProjects` directly.
export const showcaseProjects: ShowcaseProject[] = showcaseProjectsEn;


